import axiosInstance from './axiosConfig';

/**
 * ATOZAS Docs API client.
 * Every method resolves to the raw `{ success, data }` envelope the backend
 * returns, matching the convention used by labelApi/notesApi.
 */
const unwrapTemplates = (res) => {
  const payload = res?.data;
  if (payload && !Array.isArray(payload) && Array.isArray(payload.templates)) {
    return { ...res, data: payload.templates, categories: payload.categories || [] };
  }
  return { ...res, categories: res?.categories || [] };
};

const docsApi = {
  listTemplates: async (params = {}) =>
    unwrapTemplates((await axiosInstance.get('/api/docs/templates', { params })).data),

  listRecentTemplates: async () => (await axiosInstance.get('/api/docs/templates/recent')).data,

  getTemplate: async (id) => (await axiosInstance.get(`/api/docs/templates/${encodeURIComponent(id)}`)).data,

  list: async (params = {}) => (await axiosInstance.get('/api/docs', { params })).data,

  get: async (id) => (await axiosInstance.get(`/api/docs/${id}`)).data,

  sync: async (id, params = {}) => (await axiosInstance.get(`/api/docs/${id}/sync`, { params })).data,

  presence: async (id, payload = {}) =>
    (await axiosInstance.post(`/api/docs/${id}/presence`, payload)).data,

  create: async (payload = {}) => (await axiosInstance.post('/api/docs', payload)).data,

  createTab: async (documentId) =>
    (await axiosInstance.post(`/api/docs/${documentId}/tabs`)).data,

  duplicateTab: async (documentId, tabId) =>
    (await axiosInstance.post(`/api/docs/${documentId}/tabs/${encodeURIComponent(tabId)}/duplicate`)).data,

  deleteTab: async (documentId, tabId) =>
    (await axiosInstance.delete(`/api/docs/${documentId}/tabs/${encodeURIComponent(tabId)}`)).data,

  /**
   * @param {object} payload
   * @param {'auto'|'named'|'none'} payload.snapshot version-history behaviour
   */
  update: async (id, payload = {}) => (await axiosInstance.put(`/api/docs/${id}`, payload)).data,

  favorite: async (id, isFavorite) =>
    (await axiosInstance.post(`/api/docs/${id}/favorite`, { isFavorite })).data,

  duplicate: async (id) => (await axiosInstance.post(`/api/docs/${id}/duplicate`)).data,

  trash: async (id) => (await axiosInstance.post(`/api/docs/${id}/trash`)).data,

  restore: async (id) => (await axiosInstance.post(`/api/docs/${id}/restore`)).data,

  remove: async (id) => (await axiosInstance.delete(`/api/docs/${id}`)).data,

  // --- versions ---------------------------------------------------------
  listVersions: async (id, tabId) =>
    (await axiosInstance.get(`/api/docs/${id}/versions`, { params: tabId ? { tabId } : {} })).data,

  saveVersion: async (id, label, tabId) =>
    (await axiosInstance.post(`/api/docs/${id}/versions`, { label, tabId })).data,

  getVersion: async (id, versionId) =>
    (await axiosInstance.get(`/api/docs/${id}/versions/${versionId}`)).data,

  restoreVersion: async (id, versionId) =>
    (await axiosInstance.post(`/api/docs/${id}/versions/${versionId}/restore`)).data,

  // --- comments ---------------------------------------------------------
  listComments: async (id, includeResolved = true) =>
    (await axiosInstance.get(`/api/docs/${id}/comments`, { params: { includeResolved } })).data,

  addComment: async (id, payload) =>
    (await axiosInstance.post(`/api/docs/${id}/comments`, payload)).data,

  updateComment: async (id, commentId, text) =>
    (await axiosInstance.put(`/api/docs/${id}/comments/${commentId}`, { text })).data,

  replyToComment: async (id, commentId, text) =>
    (await axiosInstance.post(`/api/docs/${id}/comments/${commentId}/replies`, { text })).data,

  resolveComment: async (id, commentId, resolved = true) =>
    (await axiosInstance.post(`/api/docs/${id}/comments/${commentId}/resolve`, { resolved })).data,

  deleteComment: async (id, commentId) =>
    (await axiosInstance.delete(`/api/docs/${id}/comments/${commentId}`)).data,

  // --- sharing ----------------------------------------------------------
  updateShare: async (id, payload) =>
    (await axiosInstance.put(`/api/docs/${id}/share`, payload)).data,

  getShared: async (token) => (await axiosInstance.get(`/api/docs/shared/${token}`)).data,

  updateShared: async (token, payload = {}) =>
    (await axiosInstance.put(`/api/docs/shared/${token}`, payload)).data,

  claimShared: async (token) =>
    (await axiosInstance.post(`/api/docs/shared/${token}/claim`)).data,

  uploadImage: async (id, file) => {
    const body = new FormData();
    body.append('image', file);
    return (await axiosInstance.post(`/api/docs/${id}/images`, body)).data;
  },

  uploadSharedImage: async (token, file) => {
    const body = new FormData();
    body.append('image', file);
    return (await axiosInstance.post(`/api/docs/shared/${token}/images`, body)).data;
  },

  listSuggestions: async (id) => (await axiosInstance.get(`/api/docs/${id}/suggestions`)).data,

  syncSuggestions: async (id, items) =>
    (await axiosInstance.put(`/api/docs/${id}/suggestions`, { items })).data,

  acceptSuggestion: async (id, suggestionId) =>
    (await axiosInstance.post(`/api/docs/${id}/suggestions/${suggestionId}/accept`)).data,

  rejectSuggestion: async (id, suggestionId) =>
    (await axiosInstance.post(`/api/docs/${id}/suggestions/${suggestionId}/reject`)).data,
};

/** Absolute URL a recipient can open for a link-shared document. */
export const buildShareUrl = (token) => `${window.location.origin}/docs/shared/${token}`;

/** Resolve a stored image path so <img> hits the API host. */
export const resolveMediaUrl = (path) => {
  if (!path) return '';
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

export default docsApi;
